import { ref } from "vue";
import { defineStore } from "pinia";
import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";

export interface AzureAccount {
  id: string | number;
  [key: string]: unknown;
}

export interface NumberOfSeatsPayload {
  organisation: string | null | undefined;
  azureID: string | number | null | undefined;
}

const dedupeById = (
  data: Array<AzureAccount | null | undefined>,
): AzureAccount[] => {
  const idsSeen = new Set<string | number>();

  return (data ?? []).filter((account): account is AzureAccount => {
    if (!account) {
      return false;
    }
    if (idsSeen.has(account.id)) {
      return false;
    }
    idsSeen.add(account.id);
    return true;
  });
};

export const useAzureStore = defineStore("azure", () => {
  const azureAccounts = ref<AzureAccount[]>([]);
  const azureSeats = ref<unknown>(null);

  const getAllAzureAccounts = async (
    organisation: string | null | undefined,
  ): Promise<AzureAccount[]> => {
    const res = await $http.get(
      `${getApiClientUrl()}/v1/pt/org/${organisation}/azure`,
      { _hasCustomErrorHandling: true },
    );

    azureAccounts.value = dedupeById(res.data);

    return res.data;
  };

  const numberOfSeats = async ({
    organisation,
    azureID,
  }: NumberOfSeatsPayload): Promise<void> => {
    if (azureID) {
      const res = await $http.get(
        `${getApiClientUrl()}/v1/pt/org/${organisation}/seats/${azureID}`,
      );
      azureSeats.value = res.data;
    } else {
      azureSeats.value = undefined;
    }
  };

  const clearAzureCache = (): void => {
    azureAccounts.value = [];
    azureSeats.value = null;
  };

  return {
    azureAccounts,
    azureSeats,
    getAllAzureAccounts,
    numberOfSeats,
    clearAzureCache,
  };
});
